import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBookOpen,
  FiPackage,
  FiShield,
  FiStar,
  FiTruck,
} from "react-icons/fi";
import "../css/main.scss";

const categories = [
  { name: "Spices", note: "Whole & ground", tone: "paprika" },
  { name: "Herbs", note: "Fragrant pantry staples", tone: "herb" },
  { name: "Seasonings", note: "Blends for every dish", tone: "turmeric" },
  { name: "Dried Chiles", note: "Mild to wild heat", tone: "chile" },
  { name: "Salts & Pepper", note: "Finishing essentials", tone: "pepper" },
];

const featuredProducts = [
  {
    name: "Hungarian Smoked Paprika",
    category: "Ground spice",
    price: "$70.28",
    image: "/images/home/products/hungarian-smoked-paprika.png",
    label: "Best seller",
  },
  {
    name: "Worcestershire Sauce Powder",
    category: "Savory seasoning",
    price: "$69.16",
    image: "/images/home/products/worcestershire-powder.png",
    label: "Customer favorite",
  },
  {
    name: "Cumin Powder",
    category: "Ground spice",
    price: "$70.20",
    image: "/images/home/products/ground-cumin.png",
    label: "Pantry essential",
  },
  {
    name: "Tellicherry Black Peppercorns",
    category: "Whole spice",
    price: "$71.96",
    image: "/images/home/products/tellicherry-peppercorns.png",
    label: "On sale",
  },
  {
    name: "Vietnamese Cinnamon",
    category: "Baking spice",
    price: "$69.08",
    image: "/images/home/products/vietnamese-cinnamon.png",
    label: "Top rated",
  },
];

const recipeTopics = [
  {
    title: "Everyday spice blends",
    text: "Build layered flavor with a few smart pantry combinations.",
    image: "/images/home/recipe-blends.png",
  },
  {
    title: "Warm baking favorites",
    text: "Cinnamon-forward ideas for breads, desserts and drinks.",
    image: "/images/home/recipe-baking.png",
  },
  {
    title: "Better grilling rubs",
    text: "Savory seasonings made for smoke, char and summer cooking.",
    image: "/images/home/recipe-grilling.png",
  },
  {
    title: "Pepper, properly used",
    text: "Simple ways to get more aroma from freshly cracked pepper.",
    image: "/images/home/recipe-pepper.png",
  },
];

const serviceHighlights = [
  {
    icon: FiPackage,
    eyebrow: "Explore sickzuno",
    title: "An online spice store for curious cooks",
    text: "Discover hundreds of fragrant spices, herbs and hard-to-find culinary ingredients, packed in practical sizes for home kitchens.",
  },
  {
    icon: FiTruck,
    eyebrow: "Spicy shipping",
    title: "Flat-rate tracked delivery",
    text: "Build your pantry without the grocery-store treasure hunt. Each order is carefully packed in Hong Kong and sent with a $9.99 shipping fee.",
  },
  {
    icon: FiShield,
    eyebrow: "Spicy satisfaction",
    title: "Great flavor, guaranteed",
    text: "Fresh ingredients and thoughtful service are at the center of every order. If something is not right, our team will help make it right.",
  },
];

function Main() {
  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="main home-page">
      <main id="main-content">
        <section className="home-hero" aria-label="Featured ingredients">
          <div className="home-shell home-hero__grid">
            <article className="hero-tile hero-tile--paprika">
              <div className="hero-tile__copy">
                <p className="home-eyebrow">Bold &amp; smoky</p>
                <h1>Bulk Paprika</h1>
                <p>From sweet and mellow to rich, smoked and earthy.</p>
                <Link className="hero-tile__link" to="/shop" onClick={toTop}>
                  Shop now <FiArrowRight aria-hidden="true" />
                </Link>
              </div>
              <img src="/images/home/paprika-feature.png" alt="Smoked paprika poured into a ceramic bowl" />
            </article>

            <Link className="hero-showcase" to="/shop" onClick={toTop} aria-label="Explore all spices">
              <img src="/images/spice-hero.png" alt="Colorful spices arranged on a wooden market table" />
              <span className="hero-showcase__label">Explore 500+ ingredients</span>
            </Link>

            <article className="hero-tile hero-tile--cinnamon">
              <img src="/images/home/cinnamon-feature.png" alt="Cinnamon bark and ground Vietnamese cinnamon" />
              <div className="hero-tile__copy">
                <p className="home-eyebrow">Warm &amp; fragrant</p>
                <h2>Baking Spices</h2>
                <p>Bring depth to cakes, cookies, drinks and cozy recipes.</p>
                <Link className="hero-tile__link" to="/shop" onClick={toTop}>
                  Shop now <FiArrowRight aria-hidden="true" />
                </Link>
              </div>
            </article>
          </div>
        </section>

        <section className="popular-section" id="popular" aria-labelledby="popular-title">
          <div className="home-shell">
            <div className="home-section-heading home-section-heading--catalog">
              <h2 id="popular-title">Most popular right now</h2>
            </div>
            <div className="product-grid">
              {featuredProducts.map((product) => (
                <article className="spice-product" key={product.name}>
                  <Link className="spice-product__image-wrap" to="/shop" onClick={toTop}>
                    <span className="spice-product__badge">{product.label}</span>
                    <img
                      className="spice-product__image"
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                    />
                    <span className="spice-product__quick-view">View product</span>
                  </Link>
                  <div className="spice-product__body">
                    <p className="spice-product__category">{product.category}</p>
                    <h3>
                      <Link to="/shop" onClick={toTop}>{product.name}</Link>
                    </h3>
                    <div className="spice-product__meta">
                      <span className="spice-product__rating" aria-label="Rated five out of five">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FiStar key={star} aria-hidden="true" />
                        ))}
                      </span>
                      <strong>From {product.price}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="recommendation-section" aria-labelledby="recommendation-title">
          <div className="home-shell">
            <div className="home-section-heading home-section-heading--catalog">
              <h2 id="recommendation-title">You might also like</h2>
            </div>
            <div className="category-grid">
              {categories.map((category, index) => (
                <Link
                  className={`category-card category-card--${category.tone}`}
                  to="/shop"
                  onClick={toTop}
                  key={category.name}
                >
                  <span className="category-card__number">0{index + 1}</span>
                  <span className="category-card__name">{category.name}</span>
                  <span className="category-card__note">{category.note}</span>
                  <FiArrowRight className="category-card__arrow" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="recipe-section" aria-labelledby="recipe-title">
          <div className="home-shell">
            <div className="home-section-heading home-section-heading--center recipe-section__heading">
              <FiBookOpen aria-hidden="true" />
              <h2 id="recipe-title">Spice &amp; Herb Recipes</h2>
              <p>Featuring recipes and useful tips for foodies like you.</p>
            </div>
            <div className="recipe-grid">
              {recipeTopics.map((recipe) => (
                <Link className="recipe-card" to="/shop" onClick={toTop} key={recipe.title}>
                  <img src={recipe.image} alt="" loading="lazy" />
                  <span className="recipe-card__shade" />
                  <span className="recipe-card__copy">
                    <strong>{recipe.title}</strong>
                    <small>{recipe.text}</small>
                    <span>Read more <FiArrowRight aria-hidden="true" /></span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="story-section" aria-label="Why shop with us">
          <div className="home-shell story-list">
            {serviceHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
                <article className="story-card" key={item.title}>
                  <div className="story-card__visual" aria-hidden="true">
                    <Icon />
                  </div>
                  <div className="story-card__copy">
                    <p className="home-eyebrow">{item.eyebrow}</p>
                    <h2>{item.title}</h2>
                    <p>{item.text}</p>
                    <Link className="home-inline-link" to={index === 0 ? "/about" : index === 1 ? "/shipping" : "/contact"} onClick={toTop}>
                      {index === 0 ? "Our story" : index === 1 ? "Shipping details" : "Contact our team"}
                      <FiArrowRight aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default Main;
